import { SchemaVersion } from '@/models/schema.models';
import { RJSFSchema, UiSchema } from '@rjsf/utils';

// The idea is that we can merge the latest schema with any previous version of the same major version.
// If the major version changes, then we can can only display the existing form data using the schema
// that is saved along side it.
export const version: SchemaVersion = {
  major: 1,
  minor: 1,
};

const CreatedAt: RJSFSchema = {
  title: 'Created at',
  type: 'string',
  format: 'date-time',
};

const PoleDetails: RJSFSchema = {
  title: 'Pole',
  properties: {
    type: {
      title: 'Pole type',
      type: 'string',
      enum: ['flat', 'circular'],
    },
    senpicPostType: {
      title: 'Senpic Post Type',
      type: 'string',
      enum: ['short', 'tall'],
      description:
        'Differnt road signs will need either a short or tall senpic post',
    },
    topCover: {
      title: 'Pole top cover',
      type: 'string',
      enum: ['rubber', 'plastic', 'metal', 'welded'],
    },
    height: {
      title: 'Pole height (m)',
      type: 'number',
      enum: [3, 4, 5],
    },
    status: {
      type: 'string',
      enum: ['normal', 'weak', 'bent'],
    },
    photos: {
      title: 'Photos',
    },
    notes: {
      title: 'Notes',
      type: 'string',
    },
  },
  sunlight: {
    title: 'Sunlight',
    properties: {
      photos: {
        title: 'Photos',
      },
      notes: {
        title: 'Notes',
        type: 'string',
      },
    },
  },
  senpicMountType: {
    title: 'Senpic mount type',
    type: 'string',
    enum: ['short', 'tall'],
  },
};

// PoleDetails
const PoleDetailsUi: UiSchema = {
  'ui:order': [
    'type',
    'senpicPostType',
    'topCover',
    'height',
    'status',
    'notes',
    'photos',
  ],
  type: {
    'ui:enumNames': ['Flat', 'Circular'],
  },
  senpicPostType: {
    'ui:enumNames': ['Short', 'Tall'],
  },
  topCover: {
    'ui:enumNames': ['Rubber', 'Plastic', 'Metal', 'Welded'],
  },
  status: {
    'ui:enumNames': ['Normal', 'Weak', 'Bent'],
  },
  notes: {
    'ui:widget': 'textarea',
  },
  photos: {
    'ui:field': 'fileObject',
  },
};

const PreliminaryCheck: RJSFSchema = {
  required: ['poleAvailable'],
  properties: {
    createdAt: CreatedAt,
    poleAvailable: {
      title: 'Pole available',
      type: 'string',
      enum: ['yes', 'no'],
    },
  },
  dependencies: {
    poleAvailable: {
      oneOf: [
        {
          properties: {
            poleAvailable: {
              enum: ['yes'],
            },
            zoneType: {
              title: 'Zone type',
              type: 'string',
            },
            highTrafficPeriods: {
              title: 'High traffic periods',
              type: 'string',
              description:
                'Enter periods of the day/week to avoid school zones and busy traffic areas',
            },
            pole: PoleDetails,
            alternatePoleAvailable: {
              title: 'Alternate pole available',
              type: 'string',
              enum: ['yes', 'no'],
            },
          },
          dependencies: {
            alternatePoleAvailable: {
              oneOf: [
                {
                  properties: {
                    // poleAvailable: {
                    //   enum: ['yes'],
                    // },
                    alternatePoleAvailable: {
                      enum: ['yes'],
                    },
                    alternatePole: {
                      ...PoleDetails,
                      title: 'Alternate pole',
                    },
                  },
                },
                {
                  properties: {
                    alternatePoleAvailable: {
                      enum: ['no'],
                    },
                  },
                },
              ],
            },
          },
        },
        {
          properties: {
            poleAvailable: {
              enum: ['no'],
            },
          },
        },
      ],
    },
  },
};

const PreliminaryCheckUi: UiSchema = {
  'ui:label': false,
  'ui:order': [
    'createdAt',
    'poleAvailable',
    'zoneType',
    'highTrafficPeriods',
    'pole',
    'alternatePoleAvailable',
    'alternatePole',
  ],
  poleAvailable: {
    // It's good to separate the enum values from the UI names.
    'ui:enumNames': ['Yes', 'No'],
  },
  pole: PoleDetailsUi,
  sunlight: {
    photos: {
      'ui:field': 'fileObject',
    },
  },
  alternatePoleAvailable: {
    'ui:enumNames': ['Yes', 'No'],
  },
  alternatePole: PoleDetailsUi,
};

const PreInstall: RJSFSchema = {
  properties: {
    createdAt: CreatedAt,
    location: {
      title: 'Location',
      properties: {
        latitude: {
          title: 'Latitude',
          type: 'number',
        },
        longitude: {
          title: 'Longitude',
          type: 'number',
        },
      },
      dependencies: {
        latitude: ['longitude'],
        longitude: ['latitude'],
      },
    },
    senpicType: {
      title: 'SenPIC Type',
      type: 'string',
      enum: ['single', 'double'],
    },
    bayDirection: {
      title: 'Bay direction',
      type: 'string',
      enum: ['left', 'right', 'across'],
    },
    cameraOverlap: {
      title: 'Camera overlap',
      type: 'string',
      enum: ['yes', 'no'],
    },
  },
  dependencies: {
    senpicType: {
      oneOf: [
        {
          properties: {
            senpicType: {
              enum: ['single'],
            },
          },
        },
        {
          properties: {
            senpicType: {
              enum: ['double'],
            },
            tag: {
              title: 'Tag',
              type: 'string',
            },
          },
        },
      ],
    },
  },
};

const PreInstallUi: UiSchema = {
  'ui:label': false,
  'ui:order': [
    'createdAt',
    'location',
    'senpicType',
    'tag',
    'bayDirection',
    'cameraOverlap',
  ],
  senpicType: {
    'ui:enumNames': ['Single', 'Double'],
  },
  bayDirection: {
    'ui:enumNames': ['Left', 'Right', 'Across'],
  },
  cameraOverlap: {
    'ui:enumNames': ['Yes', 'No'],
  },
};

const PostInstall: RJSFSchema = {
  properties: {
    createdAt: CreatedAt,
    cameraViewNotes: {
      title: 'Camera view notes',
      type: 'string',
      description: 'eg: blocked by tree, sign, clear etc',
    },
    chargingNotes: {
      title: 'Charging notes',
      type: 'string',
      description: 'eg: under a tree, will fail, etc',
    },
    cameraAndPanelPhotos: {
      title: 'Camera and panel visible photos (ID visible)',
    },
    cameraAndSignPhotos: {
      title: 'Camera and sign visible photos',
    },
    cameraViewDirectionPhotos: {
      title: 'Camera view direction photos',
    },
  },
};

const PostInstallUi: UiSchema = {
  'ui:label': false,
  'ui:order': [
    'createdAt',
    'cameraViewNotes',
    'chargingNotes',
    'cameraAndPanelPhotos',
    'cameraAndSignPhotos',
    'cameraViewDirectionPhotos',
  ],
  cameraViewNotes: {
    'ui:widget': 'textarea',
  },
  chargingNotes: {
    'ui:widget': 'textarea',
  },
  cameraAndPanelPhotos: {
    'ui:field': 'fileObject',
  },
  cameraAndSignPhotos: {
    'ui:field': 'fileObject',
  },
  cameraViewDirectionPhotos: {
    'ui:field': 'fileObject',
  },
};

const ServiceNotes: RJSFSchema = {
  properties: {
    createdAt: CreatedAt,
    notes: {
      title: 'General notes',
      type: 'string',
    },
    chargingCycles: {
      title: 'Charging cycles',
      type: 'string',
    },
    fov: {
      title: 'FOV changes',
      type: 'string',
    },
    photos: {
      title: 'Photos',
    },
  },
};

const ServiceNotesUi: UiSchema = {
  'ui:label': false,
  'ui:order': ['createdAt', 'notes', 'chargingCycles', 'fov', 'photos'],
  notes: {
    'ui:widget': 'textarea',
  },
  chargingCycles: {
    'ui:widget': 'textarea',
  },
  fov: {
    'ui:widget': 'textarea',
  },
  photos: {
    'ui:field': 'fileObject',
  },
};

export const schema: RJSFSchema = {
  type: 'array',
  items: {
    required: ['stage'],

    properties: {
      stage: {
        title: 'Stage',
        type: 'string',
        enum: ['preliminaryCheck', 'preInstall', 'postInstall', 'serviceNotes'],
      },
    },
    dependencies: {
      stage: {
        oneOf: [
          {
            properties: {
              stage: {
                enum: ['preliminaryCheck'],
              },
              preliminaryCheck: PreliminaryCheck,
            },
          },
          {
            properties: {
              stage: {
                enum: ['preInstall'],
              },
              preInstall: PreInstall,
            },
          },
          {
            properties: {
              stage: {
                enum: ['postInstall'],
              },
              postInstall: PostInstall,
            },
          },
          {
            properties: {
              stage: {
                enum: ['serviceNotes'],
              },
              serviceNotes: ServiceNotes,
            },
          },
        ],
      },
    },
  },
};

export const uiSchema: UiSchema = {
  'ui:rootFieldId': 'siteData',
  items: {
    stage: {
      'ui:enumNames': [
        'Preliminary check',
        'Pre-install',
        'Post-install',
        'Service notes',
      ],
      'ui:placeholder': 'Select stage',
    },
    preliminaryCheck: PreliminaryCheckUi,
    preInstall: PreInstallUi,
    postInstall: PostInstallUi,
    serviceNotes: ServiceNotesUi,
  },
};
